import { useState } from 'react';
import Modal from '@organisms/Modal/Modal';
import Heading from '@atoms/Heading/Heading';
import { useTranslations } from 'next-intl';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { deleteSavedCard, OverriddenCustomerData } from '@api/interfaces/customerApi';
import logger from '@logger';
import trackCardPaymentOption, { CardPaymentOption } from '@helpers/analyticsHelpers/trackCardPaymentOption';
import { setSelectedCard } from '@slices/checkoutSlice';
import useCustomer from '@hooks/useCustomer';
import useAbortController from '@hooks/useAbortController';

interface Props {
  cardId: string;
  setCardId: (cardId: string) => void;
}

const DeleteCardModal = ({ cardId, setCardId }: Props) => {
  const dispatch = useAppDispatch();
  const t = useTranslations('modal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { refreshCustomer } = useCustomer();
  const { getSignal, abort } = useAbortController();

  const onClose = () => {
    abort();
    setCardId('');
  };

  const onConfirm = async () => {
    try {
      setIsSubmitting(true);
      const customerResp = await deleteSavedCard(cardId, getSignal());
      refreshCustomer(customerResp.data as OverriddenCustomerData);
      setCardId('');
      dispatch(setSelectedCard({}));
      trackCardPaymentOption(CardPaymentOption.REMOVE);
    } catch (e) {
      if (e instanceof Error) {
        logger.error({ error: `Could not delete card with id ${cardId}`, message: e.message });
      }
    }
    setIsSubmitting(false);
  };

  return (
    <>
      {cardId && (
        <Modal
          size="small"
          onClose={onClose}
          onConfirm={onConfirm}
          isSubmitting={isSubmitting}
          closeOnEscape
          cancelButtonText={t('modal.cancel')}
          confirmButtonText={t('modal.deleteCard.confirm')}
          buttonWidth={100}
        >
          <Heading variant="h3">{t('modal.deleteCard.title')}</Heading>
        </Modal>
      )}
    </>
  );
};

export default DeleteCardModal;
